import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const ImageUploader = ({ songId, existingImageUrl, existingImageKey, onImageAdded, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageUrl, setImageUrl] = useState(existingImageUrl || '');
  const [error, setError] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const fileInputRef = useRef(null);
  
  // Fetch pre-signed URL for existing image if we have a key
  useEffect(() => {
    const fetchPresignedUrl = async () => {
      if (existingImageKey) {
        try {
          const response = await axios.get(`${config.API_URL}/get-presigned-url?key=${encodeURIComponent(existingImageKey)}`);
          if (response.data && response.data.presignedUrl) {
            setImageUrl(response.data.presignedUrl);
          }
        } catch (error) {
          console.error('Error fetching pre-signed URL for image:', error);
          // Fallback to direct URL if pre-signed URL fails
          if (existingImageUrl) {
            setImageUrl(existingImageUrl);
          }
        }
      }
    };
    
    fetchPresignedUrl();
  }, [existingImageKey, existingImageUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError('');
      
      // Auto-upload when file is selected
      if (file) {
        handleUpload(file);
      }
    }
  };

  const handleUpload = async (fileToUpload) => {
    const fileToUse = fileToUpload || selectedFile;
    
    if (!fileToUse) {
      setError('Please select an image file first.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // 1) Request a pre-signed upload URL for your S3 bucket
      const fileType = fileToUse.type;
      const response = await axios.get(`${config.API_URL}/upload-url`, {
        params: {
          fileType,
          songId,
          type: 'thumbnail'
        }
      });

      const { uploadUrl, fileUrl, fileKey } = response.data;

      // 2) Use the pre-signed URL to upload the file directly to S3
      await axios.put(uploadUrl, fileToUse, {
        headers: {
          'Content-Type': fileType
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      // 3) Notify the parent so it can update the song with the new thumbnail info
      onImageAdded(fileUrl, fileKey);

      // 4) Optionally close the uploader UI
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="bg-gray-800 p-4 rounded shadow-lg">
      {/* Preview of current image with upload capability */}
      {imageUrl && (
        <div className="mb-4">
          <div 
            className="relative w-full max-w-xs mx-auto cursor-pointer group"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={triggerFileInput}
          >
            <img 
              src={imageUrl} 
              alt="Song thumbnail" 
              className="w-full h-auto rounded-lg shadow-lg border border-gray-700 transition-all duration-300 filter hover:brightness-75"
              onError={(e) => {
                console.error('Image failed to load:', imageUrl);
                e.target.onerror = null; // Prevent infinite error loop
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
                e.target.className = 'w-10 h-10 text-gray-400';
              }}
            />
            
            {/* Play button overlay */}
            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0'}`}>
              <div className="bg-black bg-opacity-50 rounded-full p-3 transform hover:scale-110 transition-transform">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
            </div>
            
            {/* Text overlay */}
            <div className={`absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-center py-2 transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0'}`}>
              Click to upload new image
            </div>
          </div>
        </div>
      )}
      
      {/* Upload form - hidden but still functional */}
      <div className={imageUrl ? "hidden" : "mb-2"}>
        <label className="block text-sm font-medium text-gray-200">
          Select New Thumbnail
        </label>
        <input 
          type="file" 
          accept="image/*" 
          onChange={handleFileChange} 
          className="mt-1 text-sm text-gray-300"
          ref={fileInputRef}
          style={imageUrl ? { display: 'none' } : {}}
        />
      </div>
      
      {/* If no image exists yet, show a placeholder that can be clicked */}
      {!imageUrl && (
        <div 
          className="mb-4 cursor-pointer" 
          onClick={triggerFileInput}
        >
          <div className="relative w-full max-w-xs mx-auto bg-gray-700 rounded-lg aspect-square flex items-center justify-center hover:bg-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-center py-2">
              Click to upload image
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="text-red-400 mb-2">{error}</div>
      )}

      {isUploading && (
        <div className="mb-3">
          <div className="w-full bg-gray-700 rounded-full h-2.5">
            <div 
              className="bg-indigo-600 h-2.5 rounded-full" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="mt-1 text-xs text-gray-400 text-center">
            Uploading: {uploadProgress}%
          </p>
        </div>
      )}

      {/* Only show upload button if there's a selected file but no auto-upload happened */}
      {selectedFile && !isUploading && !imageUrl && (
        <div className="flex space-x-2 mt-4">
          <button 
            onClick={() => handleUpload()}
            disabled={isUploading}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500 disabled:opacity-50"
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
          {onClose && (
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
            >
              Cancel
            </button>
          )}
        </div>
      )}
      
      {/* Always show cancel button if onClose is provided */}
      {onClose && !selectedFile && (
        <div className="flex justify-end mt-4">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
